/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useStore } from 'react-redux';
import merge from 'lodash.merge';

import { mergeStyleWithState } from '../../utils/mergeStyle';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import Checkbox from '../Checkbox';
import Image from '../Image';
import Input from '../Input';
import Radio from '../Radio';
import Select from '../Select';
// eslint-disable-next-line import/no-cycle
import ContainerTemplate from '../ContainerTemplate';
import Separator from '../Separator';
import Switcher from '../Switcher';
import Chart from '../Chart';
import { isNodeChild } from '../../utils/nodeTreeUtils';
import Text from '../Text';
import UnknownComponent, { isUnknownComponent } from '../UnknownComponent';
import { history } from '../../configureStore';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { getCorrespondingScheme, parseIfJSON, handleOpenUrl, runFunctionsWithTiming, isClickInsidePortal,
  replacePlaceholdersWithVariables, replacePlaceholdersWithVariablesV2, getSizesBasedOnScreen,
 } from '../../utils/base';
import { buildInternalFns } from '../../utils/handlerScope';
import { pageRequest, submitFormRequest, mergeStyleRequest, updateVariableSuccess, actionRequest } from '../../store/page/actions';
import { buildVariablesData, isVariablesChunk } from '../../utils/variableChunk';
import { StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, NodeTypeScheme, Breakpoints, ActionChunk } from '../../types/base';
import generateComponentStyles from '../../utils/styleHelper';
import { useInitEntrance } from '../../utils/useInitEntrance';
import useScrollToTopHook from '../../utils/scrollToTopHook';
import { rowSizeGlobal, colCountGlobal } from '../../config/config';

import * as style from './Container.module.scss';


const componentMapping = {
  checkbox: Checkbox,
  image: Image,
  input: Input,
  radio: Radio,
  select: Select,
  separator: Separator,
  switcher: Switcher,
  chart: Chart,
  text: Text,
};

const StyledContainer = styled.div<{
  $shape?: any;
  $baseScreenResolution?: any;
  $skipStates?: boolean;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
  ${(props) => generateComponentStyles(
    props.$shape,
    props.$baseScreenResolution,
    props.$skipStates,
    props.$parentDisplay,
    undefined,
    props.$parentFlexDirection,
  )}
`;

// Utility function to generate CSS classes with hover styles for targeted elements
const generateHoverClass = (elementId: string, hoverUpdates: any[], items?: any) => {
  if (!hoverUpdates?.length) return '';
  
  const hoverStyleUpdates = hoverUpdates?.filter((update): update is StyleUpdateChunk => update && 'style' in update) || [];
  
  // Only handle style updates that target specific child elements (have object_id and target a child)
  const targetedStyleUpdates = hoverStyleUpdates.filter(update => 
    update.object_id && update.object_id !== elementId
  );
  
  if (!targetedStyleUpdates.length) return '';
  
  const className = `hover-class-${elementId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
  // const mainElementSelector = `#${elementId.replace(/[^a-zA-Z0-9-_]/g, '\\\\$&')}`;
  
  // Generate CSS for hover state
  let css = '';
  
  if (targetedStyleUpdates.length) {
    targetedStyleUpdates.forEach((update) => {
      const { style: updateStyle, object_id: targetId } = update;
      // Use generateComponentStyles to properly convert style object to CSS
      const generatedStyles = generateComponentStyles({ display: updateStyle?.visibility === 'visible' ? 'flex' : undefined, ...updateStyle }, undefined, true);
      
      if (generatedStyles && typeof generatedStyles === 'object') {
        const cssProperties = Object.entries(generatedStyles)
          .filter(([key, value]) => value !== null && value !== undefined)
          .map(([key, value]) => `    ${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`)
          .join('\n');
        
        if (cssProperties) {
          
          // Target specific child element with data-id, nested inside main element hover
          const targetSelector = `[data-id="${targetId.replace(/"/g, '\\"')}"]`;
          // TNBEWEB-1980: gate the CSS hover behind @media (hover: hover) so a finger
          // tap on a touch device doesn't trigger (and stick) the child hover styling,
          // bypassing the JS pointerType gate.
          css += `
  @media (hover: hover) {
    .${className}:hover ${targetSelector} {
${cssProperties}
    }
  }`;
        }
      }
    });
  }
  
  // Inject CSS into document head
  const styleId = `hover-style-${elementId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.textContent = css;
  } else {
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = css;
    document.head.appendChild(styleElement);
  }
  
  return className;
};

const GridContainerComponent: React.FC<NodeTypeScheme> = (obj) => {
  const {
    id, items, style: stDraft, component,
    events: eventsDraft, dataLake,
    parentIndex,
    pageSettings,
    styleChunks,
    active_state: activeStateDraft,
    nestedTable,
    skeleton: renderSkeleton2,
    parentLoading,
    parentStyle,
    topLayerNode,
    hash,
    enableLocalShimmer,
    cache,
    settings,
  } = obj || {};
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { style: styleDraft, events, active_state, ref: realRef } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, style: stDraft, events: eventsDraft, active_state: activeStateDraft }, dataLake.data) : { ref: undefined, style: stDraft, events: eventsDraft, active_state: activeStateDraft };
  // Merge active state styles with base style
  const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
    const { indexes, refs } = chunks || {};
    let mergedStyle = style;
    
    // Apply index-based styles first
    if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
      mergedStyle = merge({}, mergedStyle, indexes[id][index]);
    }
    
    // Apply ref-based updates at the end
    if (refs && realRef) {
      const refUpdate = refs[realRef];
      if (refUpdate?.type && refUpdate.type === 'container') {
        mergedStyle = merge({}, mergedStyle, refUpdate?.update);
      }
    }
    
    return mergedStyle;
  }, [id, realRef]);


  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (active_state === 'loading' || parentLoading) ? cache : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, active_state, enableLocalShimmer, parentLoading]);

  // First useMemo to merge active state styles with base style
  const { effectiveState, entranceAnimation } = useInitEntrance(styleDraft, active_state, containerRef);
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (effectiveState && styleDraft?.states?.[effectiveState]) {
      mergedStyle = mergeStyleWithState({}, styleDraft, styleDraft.states[effectiveState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[effectiveState]) {
          acc[breakpoint] = mergeStyleWithState({}, typedBreakpointStyle, typedBreakpointStyle.states[effectiveState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    if (entranceAnimation) {
      mergedStyle = { ...mergedStyle, animation: entranceAnimation };
    }
    return mergedStyle;
  }, [styleDraft, effectiveState, entranceAnimation]);

    // Use the custom hook
  useScrollToTopHook(containerRef, hash);

  // Second useMemo to merge styles with chunks based on parentIndex
  const st = useMemo(() => mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex), [mergedStyleWithState, styleChunks, parentIndex]);
  const { row: stickyRows } = dataLake?.settings?.pin || {};
  const isSticky = parentIndex < stickyRows;
  const {
    on_click: clickUpdates,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_outclick: outclickUpdates,
    on_success: onSuccessUpdates,
    on_error: onErrorUpdates,
  } = events || {};

  const { cols: colCount, rows: rowCount } = st || {};
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const store = useStore();
  const dispatch = useDispatch();
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  // TNBEWEB-1990: tracks whether JS-applied hover styles are currently active, so a
  // tab focus/visibility loss (e.g. on_click opens a link in a new tab) can revert them.
  const isHoveredRef = useRef(false);

  // TNBEWEB-1992: shared handler scope (adds data.* / _c.* / _config.* / this.* on top of the
  // legacy $get/$set); `thisRow`/`rowPath` come from the repeatable row context (undefined otherwise).
  const internalFns = buildInternalFns({
    store,
    thisRow: dataLake?.data,
    rowPath: dataLake?.rowPath,
    owner: { id, parentIndex, row: dataLake?.data, activeState: active_state },
  });

  // Variables are assigned before every other chunk in the event (first in the
  // stack). Resolve them synchronously so the style/action/function chunks that
  // follow already see the new values. `{{this.*}}` resolves against this
  // component's own data.
  const applyVariablesFirst = (updates: any[]) => {
    const variableUpdates = (updates || []).filter((u) => isVariablesChunk(u));
    if (!variableUpdates.length) return;
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: selectVariableData(state),
      localVariables: selectLocalVariableData(state),
      thisContext: dataLake?.data,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      dispatch(updateVariableSuccess({ data }));
    }
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[], onSuccessEvts?: any[], onErrorEvts?: any[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({
        actionUpdate: update,
        componentId: id,
        onSuccess: onSuccessEvts,
        onError: onErrorEvts,
      }, filterLoadingState));
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    // for container click block requests but keep all other
    if (active_state === 'disabled' || active_state === 'loading') {
      e.stopPropagation();
      return
    };
    if (clickUpdates) {
      e.stopPropagation();
      const styleUpdates = clickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = clickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = clickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = clickUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(clickUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              },
              filterLoadingState
              ));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({
                value,
                delay,
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              },
              filterLoadingState
              ));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  // Recursive function to check if a target node is a child of the current container
  const isTargetNodeChild = useCallback((targetId: string): boolean => isNodeChild(targetId, items), [items]);

  const handleHover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply hover
    if (hoverUpdates) {
      isHoveredRef.current = true; // TNBEWEB-1990: JS hover styles are being applied
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(hoverUpdates);
      
      // Only dispatch style updates that are NOT handled by CSS hover classes
      // (i.e., updates that don't target specific child elements or target the container itself)
      const styleUpdatesForDispatch = styleUpdates.filter(update => {
        // Exclude if object_id matches {{...}} or contains {{...}} pattern
        if (/\{\{.*\}\}/.test(update.object_id)) return true;
        // If no object_id, it targets the container itself
        if (!update.object_id) return true;
        
        // If object_id matches current container, include it
        if (update.object_id === (realRef || id)) return true;
        
        // If object_id targets a child node, exclude it (let CSS handle it)
        if (isTargetNodeChild(update.object_id)) return false;
        
        // Otherwise, include it (might target parent or sibling)
        return true;
      });
      
      const runEffects = () => {
        if (styleUpdatesForDispatch.length) {
          dispatch(mergeStyleRequest(styleUpdatesForDispatch, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  // TNBEWEB-1990: pointer-event-free unhover core, callable both from the
  // onPointerLeave handler and from the tab focus/visibility fallback below.
  const applyUnhover = (e?: React.PointerEvent<HTMLDivElement>) => {
    isHoveredRef.current = false;
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(unhoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  const handleUnhover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply unhover
    applyUnhover(e);
  }

  // TNBEWEB-1990: when an on_click opens a link in a new tab, the new tab steals
  // focus BEFORE the pointer leaves the element, so the browser never fires
  // pointerleave → handleUnhover never runs → JS hover styles stay stuck (red).
  // Revert them when the tab loses focus / becomes hidden while still hovered.
  useEffect(() => {
    if (!unhoverUpdates?.length) return undefined;
    const resetHoverIfActive = () => {
      if (isHoveredRef.current) applyUnhover();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') resetHoverIfActive();
    };
    window.addEventListener('blur', resetHoverIfActive);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('blur', resetHoverIfActive);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [unhoverUpdates]);

  useEffect(() => {
    if (launchExecutedRef.current !== st?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = st?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(launchUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              // const newTimeout = setTimeout(() => {
              //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
              // }, config?.delay * 1000 || 0);

              // timeoutsRef.current.push(newTimeout);
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects);
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, st?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
   if (!outclickUpdates?.length) return undefined;
   if (st?.visibility === 'hidden') return undefined;

    const handleUnclick = (e: MouseEvent) => {
      if (active_state === 'disabled' || active_state === 'loading') return;
      if (
        outclickUpdates?.length &&
        containerRef.current && 
        !containerRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        const runEffects = () => {
          // Apply style updates
          if (filteredUpdates.styleUpdates.length) {
            dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex, dataLake?.data));
          }

          // Handle action updates
          if (filteredUpdates.actionUpdates.length) {
            handleActionChunk(filteredUpdates.actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }

          // Process request updates
          if (filteredUpdates.requestUpdates.length) {
            filteredUpdates.requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'action') {
                dispatch(submitFormRequest({
                  value,
                  silent,
                  delay,
                  componentId: id
                }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        applyVariablesFirst(outclickUpdates);
        runFunctionsWithTiming(filteredUpdates.functionUpdates, internalFns, runEffects);
      }
    };
    
    document.addEventListener('mousedown', handleUnclick, true);
    return () => {
      document.removeEventListener('mousedown', handleUnclick, true);
    };
  }, [
    parentIndex, 
    active_state, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
    st?.visibility
  ]);

  // Generate hover class name when component loads or id changes
  const hoverClassName = useMemo(() => generateHoverClass(realRef || id, hoverUpdates), [id, realRef, hoverUpdates]);

  // Cleanup hover styles when component unmounts
  useEffect(() => {
    if (!id) return undefined;
    const currentId = id;
    return () => {
      // Check if any other elements with the same data-id still exist in the DOM
      const elementsWithSameId = document.querySelectorAll(`[data-id="${currentId}"]`);
      
      // Only remove the hover styles if no other elements with this id exist
      if (elementsWithSameId?.length === 0) {
        const styleId = `hover-style-${currentId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      }
    };
  }, [id]);

  // State transitions (:hover / :active / active_state) animate via the CSS
  // `transition` emitted in generateComponentStyles. No framer-motion entrance.
  const ContainerEl = StyledContainer;

  return (
    <ContainerEl
      ref={containerRef}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      style={{
        gridTemplateColumns: `repeat(${colCount || colCountGlobal}, 1fr)`,
        gridTemplateRows: rowCount ? `repeat(${rowCount}, 1fr)` : undefined,
        gridAutoRows: rowCount ? undefined : `${rowSizeGlobal}px`,
        position: isSticky ? 'sticky' : undefined,
        maxWidth: topLayerNode ? '100vw' : undefined,
        top: isSticky ? 0 : undefined,
        zIndex: isSticky ? `${99999999 - parentIndex}` : undefined,
        visibility: renderSceleton && st ? 'hidden' : undefined,
      }}
      data-id={realRef || id}
      $shape={st}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={active_state === 'disabled' || active_state === 'loading'}
      $parentDisplay={parentStyle?.display}
      $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
      data-component={component}
      onClick={handleClick}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      className={hoverClassName}
    >
      {items && items.map((item, index) => {
        const Component = componentMapping[item.component];
        const commonProps = {
          dataLake: getCorrespondingScheme(item, obj, dataLake, index, pageSettings?.variables, parentIndex),
          parentIndex,
          pageSettings,
          styleChunks,
          nestedTable,
          parentStyle: st,
          parentLoading: active_state === 'loading' || parentLoading,
          enableLocalShimmer,
        };

        if (item.component === 'container' && item.type === 'table-body-row' && item.dynamic) {
          const replaced = replacePlaceholdersWithVariables(commonProps?.dataLake?.settings?.source, pageSettings?.variables);
          const parseSourceIfExist = parseIfJSON(replaced);
          return Array.isArray(parseSourceIfExist) ? parseSourceIfExist?.map((sourceItem, sourceIndex) => {
            const dynamicProps = {
              dataLake: getCorrespondingScheme(item, obj, dataLake, sourceIndex, pageSettings?.variables, parentIndex),
              parentIndex: sourceIndex,
              styleChunks,
              nestedTable: true,
              parentLoading: active_state === 'loading' || parentLoading,
              enableLocalShimmer,
            };
            // The guard above pins item.component === 'container', and componentMapping
            // has no 'container' (or 'table') key — so Component is always undefined here
            // and this row is always a ContainerTemplate. If that guard is ever widened,
            // reinstate the per-component branching.
            return <ContainerTemplate key={`${item.id}::${sourceIndex}`} {...item} {...dynamicProps} hash={hash} />;
          }) : null;
        }

        if (Component) {
          return <Component key={item.id} {...item} {...commonProps} parentRef={containerRef} />;
        } if (item.component === 'container') {
          return <ContainerTemplate key={item.id} {...item} {...commonProps} hash={hash} />;
        } if (item.component === 'table') {
          return <ContainerTemplate key={item.id} {...item} {...commonProps} hash={hash} />;
        } if (isUnknownComponent(item.component)) {
          return <UnknownComponent key={item.id} id={item.id} component={item.component} style={item.style} parentStyle={st} />;
        }
        return null;
      })}
      {(renderSceleton && st) ? (
        <ContainerSkeleton
          style={{...st, display: st.display === 'grid' ? 'flex' : st.display }}
          baseScreenResolution={baseScreenResolution}
          skipStates={active_state === 'disabled' || active_state === 'loading'}
          nested
        />
      ) : null}
    </ContainerEl>
  );

}

export default GridContainerComponent;
